import * as React from "react";
import { useForm, useFieldArray, useWatch, Control } from "react-hook-form";
import {
  Button,
  Grid,
  Title,
  Text,
  Tabs,
  SegmentedControl,
  Modal,
  ActionIcon,
  Flex,
  Stack,
  Divider,
  TextInput,
  Group,
} from "@mantine/core";
import { CTextInput, CMultiSelect, CSelect, CTextarea, CRadioGroup } from "@supportinitiative/react-hook-form-mantine";
import { object, string, number, date, array, setLocale } from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { getConfirmationStatus, getSystemStatus } from "../../../UseCaseUtils";
import { UseCaseCVMContext } from "../../../UseCaseCVMContext";
import { UAction, UStatusUpdateActionType, UseCaseType } from "@supportinitiative/common";

setLocale({
  mixed: {
    required: "Required",
    notType: "Invalid Value",
  },
});

function getDatasetsLOV(recon?: UseCaseType["definition"]["recons"][number]) {
  if (recon) {
    if (recon.definition.datasets) {
      const datasets = recon.definition.datasets.map((d) => {
        return {
          value: d.id,
          label: `${d.name} [ ${d.id} ]`,
        };
      });
      return datasets;
    } else {
      return [];
    }
  } else {
    return [];
  }
}

const UStatusUpdateActionSchema = object({
  id: string().required(),
  name: string().required(),
  type: string().required().oneOf(["STATUS_UPDATE"]),
  description: string().nullable(),
  definition: object({
    dataset: string().required(),
    where: string().nullable(),
    systemStatus: string().required(),
    confirmationStatus: string().required(),
    userStatus: array().of(string().required()),
    statusOverride: string().required().oneOf(["OVERRIDE", "APPEND", "IGNORE"]),
  }),
});

function StatusUpdate({
  recon,
  save,
  cancel,
  action,
}: {
  cancel: () => void;
  save: (values: any) => void;
  action: any;
  recon?: UseCaseType["definition"]["recons"][number];
}) {
  const { state: useCaseFormState } = React.useContext(UseCaseCVMContext);

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    getValues,
    setError,
    clearErrors,
    formState: { errors },
  } = useForm<UAction<UStatusUpdateActionType>>({
    defaultValues: {
      ...action,
      definition: {
        ...action.definition,
      },
    },
    criteriaMode: "all",
    resolver: yupResolver(UStatusUpdateActionSchema),
  });

  return (
    <Modal centered size="xl" opened={true} onClose={cancel} title={<Title order={4}>Data Load</Title>}>
      <form
        onSubmit={(event) => {
          event.preventDefault();
          // handleSubmit(onSubmitForm)();
          event.stopPropagation();
          console.log("inside child");
          // cancel();
        }}
        noValidate
      >
        <Grid align="flex-start" gutter={"xs"} mb="lg" style={{ display: "flex", alignItems: "flex-end" }}>
          <Grid.Col span={6}>
            <CTextInput
              name="name"
              control={control}
              label="Name"
              required
              showErrorText={false}
              disabled={useCaseFormState.actionOptions.disableAll}
            />
          </Grid.Col>
          <Grid.Col span={6}>
            <CTextInput name="id" control={control} label="ID" required showErrorText={false} disabled={true} />
          </Grid.Col>
        </Grid>

        <Grid align="flex-start" gutter={"xs"} mb="lg" style={{ display: "flex", alignItems: "flex-end" }}>
          <Grid.Col span={12}>
            <CTextarea
              name="description"
              minLength={5}
              control={control}
              label="Description"
              showErrorText={false}
              disabled={useCaseFormState.actionOptions.disableAll}
            />
          </Grid.Col>
        </Grid>
        <Divider my="xs" label="Dataset" />
        <Grid align="flex-start" gutter={"xs"} mb="lg" style={{ display: "flex", alignItems: "flex-end" }}>
          <Grid.Col span={12}>
            <CSelect
              name={`definition.dataset`}
              control={control}
              label={"Dataset"}
              data={getDatasetsLOV(recon)}
              showErrorText={false}
              disabled={useCaseFormState.actionOptions.disableAll}
            />
          </Grid.Col>
        </Grid>

        <Divider my="xs" label="Where" labelPosition="center" />

        <Grid align="stretch" gutter={"xs"} mb="xs">
          <Grid.Col span={12}>
            <CTextarea
              autosize
              minRows={2}
              name={`definition.where`}
              control={control}
              label={"Condition"}
              disabled={useCaseFormState.actionOptions.disableAll}
            />
          </Grid.Col>
        </Grid>

        <Divider my="xs" label="Status" labelPosition="center" />

        <Grid align="stretch" gutter={"xs"} mb="xs">
          <Grid.Col span={4}>
            <CSelect
              name={`definition.systemStatus`}
              control={control}
              label="Status"
              data={getSystemStatus("ALL")}
              showErrorText={false}
              searchable
              required
              disabled={useCaseFormState.actionOptions.disableAll}
            />
          </Grid.Col>
          <Grid.Col span={4}>
            <CSelect
              name={`definition.confirmationStatus`}
              control={control}
              label="Confirmation Status"
              data={getConfirmationStatus()}
              showErrorText={false}
              searchable
              required
              disabled={useCaseFormState.actionOptions.disableAll}
            />
          </Grid.Col>
          <Grid.Col span={4}>
            <CMultiSelect
              name={`definition.userStatus`}
              control={control}
              label="User Status"
              searchable
              data={useCaseFormState.userStatus || []}
              showErrorText={false}
              disabled={useCaseFormState.actionOptions.disableAll}
            />
          </Grid.Col>
        </Grid>
        <Divider my="xs" label="Status Override" labelPosition="center" />

        <Grid align="stretch" gutter={"xs"} mb="xs">
          <Grid.Col span={12}>
            <CRadioGroup
              name={`definition.statusOverride`}
              control={control}
              data={[
                { label: "Ignore", value: "IGNORE" },
                /* { label: "Override", value: "OVERRIDE" }, */
                { label: "Append", value: "APPEND" },
              ]}
              disabled={useCaseFormState.actionOptions.disableAll}
            />
          </Grid.Col>
        </Grid>

        <Grid align="stretch" gutter={"xs"} mb="xs">
          <Grid.Col span={2}>
            <Button
              color="teal"
              variant="filled"
              fullWidth
              type="submit"
              onClick={
                useCaseFormState.actionOptions.disableAll
                  ? () => {
                      console.log("only cancel");
                      cancel();
                    }
                  : async (event: React.MouseEvent<HTMLButtonElement>) => {
                      event.preventDefault();
                      const values = getValues();
                      // console.log("values", values);
                      clearErrors();
                      try {
                        await UStatusUpdateActionSchema.validate(values, {
                          abortEarly: false,
                        });

                        console.log("values", values);
                        save?.(values);
                        //cancel();
                      } catch (error) {
                        console.log("errors", error);
                        console.log("errors", JSON.stringify(error));
                        console.log("error inner", JSON.stringify(error));
                        (error as any).inner.forEach((err: any) => {
                          setError(err.path, {
                            type: "custom",
                            message: err.message,
                          });
                        });
                      }
                    }
              }
            >
              Ok
            </Button>
          </Grid.Col>
        </Grid>
      </form>
    </Modal>
  );
}

export default StatusUpdate;
